declare global {
  interface Window {
    IS24?: {
      TEALIUM: {
        tracking: {
          report: ReportFn;
        };
      };
    };
  }
}

const REPORT_TYPE_UTAG_VIEW = {
  reportType: 'UTAG_VIEW',
};

const MAX_TRACKING_RETRIES = 10;
const TEALIUM_RETRY_INTERVAL = 1000;
const PAGE_TITLE_PREFIX = 'is24.de.account.verwaltung.firmendaten.teammanagement';
const DEFAULT_GA_CATEGORY = 'user';
const DEFAULT_GA_ACTION = 'teammanagement';
const DEFAULT_EVENT_PRODUCT = 'user';
const MODULE_NAME = 'scoutmanager';
const SCRIPT_URL_REGEX = /^(?:https?:)?\/\/tracking\.(?:sandbox-)?immobilienscout24\.de\/tr\.js.*/i;
const scriptUrl = () =>
  `//tracking.${process.env.BASE_DOMAIN}/tr.js?cP--countername=is24.de.account.verwaltung.firmendaten.teammanagement&cP--svc_module_name=${MODULE_NAME}&addOnClick&initialPageView=false`;

interface ReportingQueueElement {
  event: Record<string, string>;
  args?: Record<string, string>;
}

const reportingQueue: Array<ReportingQueueElement> = [];
const report = (event: Record<string, string>, args?: Record<string, string>): void => {
  reportingQueue.push({
    event,
    args,
  });

  if (!window.IS24?.TEALIUM?.tracking?.report) {
    console.log('Tealium report function is not available');
    return;
  } else {
    reportEventsQueue();
  }
};

export const reportEventsQueue = (): void => {
  let element = reportingQueue.shift();
  while (element && window.IS24?.TEALIUM?.tracking?.report) {
    window.IS24.TEALIUM.tracking.report(element.event, element.args);
    element = reportingQueue.shift();
  }
};

interface ReportViewEventArgs {
  pageTitle?: string;
  completePath?: string;
}

const reportViewEvent = (args: ReportViewEventArgs): void => {
  const event = {
    svc_module_name: MODULE_NAME,
    pag_pagetitle: args.completePath ? args.completePath : `${PAGE_TITLE_PREFIX}.${args.pageTitle}`,
  };

  report({ ...event }, REPORT_TYPE_UTAG_VIEW);
};

interface ReportActionEventArgs {
  pageTitle?: string;
  completePath?: string;
  category?: string;
  action?: string;
  label: string;
  event_name: string;
  event_product?: string;
}

const reportActionEvent = (args: ReportActionEventArgs): void => {
  const event = {
    pag_pagetitle: args.completePath ? args.completePath : `${PAGE_TITLE_PREFIX}.${args.pageTitle}`,
    evt_ga_category: args.category ? args.category : DEFAULT_GA_CATEGORY,
    evt_ga_action: args.action ? args.action : DEFAULT_GA_ACTION,
    evt_ga_label: args.label,
    event_name: args.event_name,
    event_product: args.event_product ? args.event_product : DEFAULT_EVENT_PRODUCT,
  };

  report({ ...event });
};

export const isTrackingScriptInDom = (): boolean => {
  const scriptElements = [...document.querySelectorAll('script')];
  return scriptElements.find((scriptElement) => SCRIPT_URL_REGEX.test(scriptElement.src)) !== undefined;
};

enum TealiumState {
  INITIALIZED = 'INITIALIZED',
  UNINITIALIZED = 'UNINITIALIZED',
}

const getTealiumState = (retryTimeout = TEALIUM_RETRY_INTERVAL): Promise<TealiumState> => {
  console.log('Getting Tealium state');
  return new Promise((resolve) => {
    let intervalTriggerCounter = 0;
    const interval = setInterval(() => {
      intervalTriggerCounter++;
      if (intervalTriggerCounter <= MAX_TRACKING_RETRIES) {
        if (window.IS24?.TEALIUM?.tracking?.report) {
          clearInterval(interval);
          console.log('Reporting to events queue');
          reportEventsQueue();
          resolve(TealiumState.INITIALIZED);
        }
      } else {
        console.log('Cannot initialized TEALIUM.');
        clearInterval(interval);
        resolve(TealiumState.UNINITIALIZED);
      }
    }, retryTimeout);
  });
};

const addTrackingScript = (): void => {
  if (process.env.STAGE === 'dev') {
    console.log('Initializing Tealium Mock');
    return;
  }
  const script = document.createElement('script');
  script.src = scriptUrl();
  script.async = true;
  document.body.appendChild(script);
};

export { reportViewEvent, ReportViewEventArgs, reportActionEvent, ReportActionEventArgs, addTrackingScript, getTealiumState, TealiumState };
